import React, { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress, Chip, Divider, Link } from "@mui/material";
import { ToastContainer } from "react-toastify";
import {
  BaseBox,
  BaseButton,
  BaseCheckbox,
  BaseTextField,
  BaseTypography,
} from "../../Common/UI/exports";
import {
  fetchPrManagedRepositories,
  fetchRepositoryBranches,
  managePrFiles,
  previewSourcePullRequest,
} from "../../APIs/PrManagementAPI";

const OPERATIONS = ["CREATE", "UPDATE", "DELETE"];
const CHANGE_SOURCE_TYPES = [
  { label: "Manual file change", value: "MANUAL" },
  { label: "Clone PR as patch", value: "CLONE_PR" },
];
const SOURCE_BRANCH_MODES = [
  { label: "Create new branch", value: "CREATE_NEW" },
  { label: "Use existing branch", value: "USE_EXISTING" },
];
const CHANGE_TYPES = [
  { label: "Full file replace", value: "FULL_FILE_REPLACE" },
  { label: "Find and replace", value: "FIND_AND_REPLACE" },
  { label: "Pattern replace", value: "PATTERN_REPLACE" },
];
const RESULT_STATUS_ORDER = {
  CREATED: 1,
  FAILED: 2,
  SKIPPED: 3,
};
const MANY_REPOS_THRESHOLD = 10;
const PROTECTED_SOURCE_BRANCHES = ["main"];
const PROTECTED_TARGET_BRANCHES = ["main"];

const newFileChange = () => ({
  id: crypto.randomUUID(),
  filePath: "",
  changeType: "FIND_AND_REPLACE",
  sourceContent: "",
  replacementContent: "",
  content: "",
});

const encodeText = (value = "") => {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
};

const getErrorMessage = (error, context = {}) => {
  const responseData = error?.response?.data;
  const status = error?.response?.status;
  const statusText = error?.response?.statusText;
  const fallback = status
    ? `Request failed with status code ${status}${statusText ? ` ${statusText}` : ""}`
    : error.message;
  const hasEmptyResponseBody = responseData === "" || responseData === null || responseData === undefined;

  if (hasEmptyResponseBody && context.changeSourceType === "CLONE_PR") {
    return [
      "Operation failed: clone-from-PR request returned 400 with an empty response body.",
      "Check that the source PR URL is complete and accessible, the PR belongs to Azure DevOps project VF_Itaap, and the PR contains supported added, modified, or deleted text files.",
      context.sourcePullRequestUrl ? `Source PR URL: ${context.sourcePullRequestUrl}` : "",
    ].filter(Boolean).join(" ");
  }

  if (typeof responseData === "string") {
    try {
      const parsedResponseData = JSON.parse(responseData);
      return getErrorMessage({ ...error, response: { ...error.response, data: parsedResponseData } }, context);
    } catch (parseError) {
      return responseData || fallback;
    }
  }

  const message = (
    responseData?.message ||
    responseData?.detail ||
    responseData?.error ||
    responseData?.details ||
    responseData?.description ||
    responseData?.title ||
    fallback
  );
  return message?.startsWith("Operation failed:")
    ? message
    : `Operation failed: ${message}`;
};

const getResultStatus = (repoResult) => (repoResult.status || "UNKNOWN").toUpperCase();

function PrManagement() {
  const [repositories, setRepositories] = useState([]);
  const [selectedRepositoryIds, setSelectedRepositoryIds] = useState([]);
  const [repositorySearch, setRepositorySearch] = useState("");
  const [targetBranch, setTargetBranch] = useState("main");
  const [branchesByRepositoryId, setBranchesByRepositoryId] = useState({});
  const [sourceBranchesByRepositoryId, setSourceBranchesByRepositoryId] = useState({});
  const [targetBranchesByRepositoryId, setTargetBranchesByRepositoryId] = useState({});
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchRefreshKey, setBranchRefreshKey] = useState(0);
  const [sourceBranchMode, setSourceBranchMode] = useState("CREATE_NEW");
  const [resetSourceBranchFromTarget, setResetSourceBranchFromTarget] = useState(true);
  const [newSourceBranchName, setNewSourceBranchName] = useState("");
  const [existingSourceBranch, setExistingSourceBranch] = useState("");
  const [changeSourceType, setChangeSourceType] = useState("MANUAL");
  const [sourcePullRequestUrl, setSourcePullRequestUrl] = useState("");
  const [operation, setOperation] = useState("UPDATE");
  const [fileChanges, setFileChanges] = useState([newFileChange()]);
  const [title, setTitle] = useState("");
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copyMessage, setCopyMessage] = useState("");
  const [showSelectedRepositories, setShowSelectedRepositories] = useState(false);
  const [sourcePullRequestPreview, setSourcePullRequestPreview] = useState(null);
  const [sourcePullRequestPreviewLoading, setSourcePullRequestPreviewLoading] = useState(false);
  const [sourcePullRequestPreviewError, setSourcePullRequestPreviewError] = useState("");

  useEffect(() => {
    loadRepositories();
  }, []);

  const selectedRepositories = useMemo(
    () => repositories.filter((repo) => selectedRepositoryIds.includes(repo.id)),
    [repositories, selectedRepositoryIds]
  );

  const filteredRepositories = useMemo(() => {
    const query = repositorySearch.trim().toLowerCase();
    if (!query) return repositories;
    return repositories.filter((repo) => repo.name?.toLowerCase().includes(query));
  }, [repositories, repositorySearch]);

  const sortedRepositoryResults = useMemo(() => {
    const repositoryResults = result?.repositoryResults || [];
    return [...repositoryResults].sort((left, right) => {
      const leftRank = RESULT_STATUS_ORDER[getResultStatus(left)] || 99;
      const rightRank = RESULT_STATUS_ORDER[getResultStatus(right)] || 99;
      if (leftRank !== rightRank) return leftRank - rightRank;
      return (left.repositoryName || "").localeCompare(right.repositoryName || "");
    });
  }, [result]);

  const resultCounts = useMemo(() => {
    return sortedRepositoryResults.reduce(
      (counts, repoResult) => {
        const status = getResultStatus(repoResult);
        return {
          ...counts,
          total: counts.total + 1,
          created: counts.created + (status === "CREATED" ? 1 : 0),
          failed: counts.failed + (status === "FAILED" ? 1 : 0),
          skipped: counts.skipped + (status === "SKIPPED" ? 1 : 0),
          other: counts.other + (!["CREATED", "FAILED", "SKIPPED"].includes(status) ? 1 : 0),
        };
      },
      { total: 0, created: 0, failed: 0, skipped: 0, other: 0 }
    );
  }, [sortedRepositoryResults]);

  const suggestedTitle = useMemo(() => {
    if (changeSourceType === "CLONE_PR") {
      return sourcePullRequestUrl.trim() ? "Clone PR as patch" : "";
    }

    const firstChange = fileChanges[0];
    if (!firstChange?.filePath) return "";

    if (operation === "UPDATE" && firstChange.changeType === "FIND_AND_REPLACE") {
      const versionMatch = firstChange.replacementContent?.match(/<version>\s*([^<]+)\s*<\/version>/i);
      if (firstChange.replacementContent?.toLowerCase().includes("apifactory-cache") && versionMatch?.[1]) {
        return `Update apifactory-cache to ${versionMatch[1]}`;
      }
      if (versionMatch?.[1]) return `Update ${firstChange.filePath} to ${versionMatch[1]}`;
      return `Update ${firstChange.filePath}`;
    }

    if (operation === "CREATE") return `Create ${firstChange.filePath}`;
    if (operation === "UPDATE") return `Update ${firstChange.filePath}`;
    if (operation === "DELETE") return `Delete ${firstChange.filePath}`;
    return "";
  }, [changeSourceType, fileChanges, operation, sourcePullRequestUrl]);

  const prSummary = useMemo(() => {
    if (sortedRepositoryResults.length === 0) return "";

    return sortedRepositoryResults
      .map((repoResult) => repoResult.pullRequestUrl)
      .filter(Boolean)
      .join("\n");
  }, [sortedRepositoryResults]);

  useEffect(() => {
    let active = true;

    const loadBranches = async () => {
      if (selectedRepositories.length === 0) {
        setBranchesByRepositoryId({});
        setSourceBranchesByRepositoryId({});
        setTargetBranchesByRepositoryId({});
        setTargetBranch("main");
        setExistingSourceBranch("");
        return;
      }

      setBranchesLoading(true);

      try {
        const branchesByRepo = await Promise.all(
          selectedRepositories.map(async (repo) => ({
            repo,
            branches: (await fetchRepositoryBranches(repo.id)) || [],
          }))
        );

        if (!active) return;

        const nextBranchesByRepositoryId = branchesByRepo.reduce((branchesMap, item) => ({
          ...branchesMap,
          [item.repo.id]: item.branches,
        }), {});
        const commonBranches = branchesByRepo.reduce((common, item, index) => {
          const normalizedBranches = item.branches || [];
          if (index === 0) return normalizedBranches;
          return common.filter((branch) => normalizedBranches.includes(branch));
        }, []);

        setBranchesByRepositoryId(nextBranchesByRepositoryId);
        setTargetBranch((currentBranch) => chooseBranch(currentBranch, commonBranches));
        setExistingSourceBranch((currentBranch) => chooseBranch(currentBranch, commonBranches));
        setSourceBranchesByRepositoryId((currentSources) =>
          selectedRepositories.reduce((sources, repo) => {
            return {
              ...sources,
              [repo.id]: currentSources[repo.id] || "",
            };
          }, {})
        );
        setTargetBranchesByRepositoryId((currentTargets) =>
          selectedRepositories.reduce((targets, repo) => {
            const repoBranches = nextBranchesByRepositoryId[repo.id] || [];
            return {
              ...targets,
              [repo.id]: chooseTargetBranch(currentTargets[repo.id] || targetBranch, repoBranches),
            };
          }, {})
        );
      } catch (branchError) {
        if (!active) return;
        setBranchesByRepositoryId({});
        setSourceBranchesByRepositoryId({});
        setTargetBranchesByRepositoryId({});
        setTargetBranch("");
        setExistingSourceBranch("");
        setError(getErrorMessage(branchError));
      } finally {
        if (active) {
          setBranchesLoading(false);
        }
      }
    };

    loadBranches();

    return () => {
      active = false;
    };
  }, [selectedRepositories, branchRefreshKey]);

  useEffect(() => {
    if (!titleManuallyEdited && suggestedTitle) {
      setTitle(suggestedTitle);
    }
  }, [suggestedTitle, titleManuallyEdited]);

  useEffect(() => {
    let active = true;
    const trimmedUrl = sourcePullRequestUrl.trim();
    if (changeSourceType !== "CLONE_PR" || !trimmedUrl.includes("/_git/") || !trimmedUrl.includes("/pullrequest/")) {
      setSourcePullRequestPreview(null);
      setSourcePullRequestPreviewError("");
      setSourcePullRequestPreviewLoading(false);
      return undefined;
    }

    setSourcePullRequestPreviewLoading(true);
    setSourcePullRequestPreviewError("");
    const timeoutId = setTimeout(async () => {
      try {
        const preview = await previewSourcePullRequest(trimmedUrl);
        if (!active) return;
        setSourcePullRequestPreview(preview);
      } catch (previewError) {
        if (!active) return;
        setSourcePullRequestPreview(null);
        setSourcePullRequestPreviewError(getErrorMessage(previewError, { changeSourceType, sourcePullRequestUrl: trimmedUrl }));
      } finally {
        if (active) {
          setSourcePullRequestPreviewLoading(false);
        }
      }
    }, 500);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [changeSourceType, sourcePullRequestUrl]);

  const chooseBranch = (currentBranch, branches) => {
    if (branches.includes(currentBranch)) return currentBranch;
    if (branches.includes("main")) return "main";
    if (branches.includes("master")) return "master";
    if (branches.includes("develop")) return "develop";
    return branches[0] || "";
  };

  const isProtectedSourceBranch = (branchName) =>
    PROTECTED_SOURCE_BRANCHES.includes((branchName || "").trim().toLowerCase());

  const isProtectedTargetBranch = (branchName) =>
    PROTECTED_TARGET_BRANCHES.includes((branchName || "").trim().toLowerCase());

  const getSourceBranchOptions = (repo) =>
    (branchesByRepositoryId[repo.id] || []).filter((branch) => !isProtectedSourceBranch(branch));

  const getTargetBranchOptions = (repo) =>
    (branchesByRepositoryId[repo.id] || []).filter((branch) => !isProtectedTargetBranch(branch));

  const chooseSourceBranch = (currentBranch, branches) => {
    const sourceBranches = branches.filter((branch) => !isProtectedSourceBranch(branch));
    return chooseBranch(currentBranch, sourceBranches);
  };

  const chooseTargetBranch = (currentBranch, branches) => {
    const targetBranches = branches.filter((branch) => !isProtectedTargetBranch(branch));
    return chooseBranch(currentBranch, targetBranches);
  };

  const getGlobalSourceBranchName = () =>
    sourceBranchMode === "USE_EXISTING" ? existingSourceBranch : newSourceBranchName;

  const getRepositorySourceBranch = (repo) => {
    if (sourceBranchesByRepositoryId[repo.id]) return sourceBranchesByRepositoryId[repo.id];
    if (sourceBranchMode === "USE_EXISTING") {
      return chooseSourceBranch(existingSourceBranch, branchesByRepositoryId[repo.id] || []);
    }
    return getGlobalSourceBranchName();
  };

  const getRepositoryTargetBranch = (repo) =>
    targetBranchesByRepositoryId[repo.id] || chooseTargetBranch(targetBranch, branchesByRepositoryId[repo.id] || []);

  const updateRepositorySourceBranch = (repositoryId, branchName) => {
    setSourceBranchesByRepositoryId((currentSources) => ({
      ...currentSources,
      [repositoryId]: branchName,
    }));
  };

  const updateRepositoryTargetBranch = (repositoryId, branchName) => {
    setTargetBranchesByRepositoryId((currentTargets) => ({
      ...currentTargets,
      [repositoryId]: branchName,
    }));
  };

  const loadRepositories = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchPrManagedRepositories();
      setRepositories((data || []).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  };

  const toggleRepository = (repositoryId) => {
    setSelectedRepositoryIds((current) =>
      current.includes(repositoryId)
        ? current.filter((id) => id !== repositoryId)
        : [...current, repositoryId]
    );
  };

  const refreshBranches = () => {
    setBranchRefreshKey((current) => current + 1);
  };

  const updateFileChange = (id, field, value) => {
    setFileChanges((current) =>
      current.map((fileChange) =>
        fileChange.id === id ? { ...fileChange, [field]: value } : fileChange
      )
    );
  };

  const removeFileChange = (id) => {
    setFileChanges((current) =>
      current.length === 1 ? current : current.filter((fileChange) => fileChange.id !== id)
    );
  };

  const validate = () => {
    if (selectedRepositories.length === 0) return "Select at least one repository.";
    if (changeSourceType !== "CLONE_PR") {
      for (const repo of selectedRepositories) {
        const repoTargetBranch = getRepositoryTargetBranch(repo);
        if (!repoTargetBranch) return `Target branch is required for ${repo.name}.`;
        const repoTargetBranchValidation = validateBranchName(repoTargetBranch, `${repo.name} target branch`);
        if (repoTargetBranchValidation) return repoTargetBranchValidation;
        if (isProtectedTargetBranch(repoTargetBranch)) {
          return `Target branch '${repoTargetBranch.trim()}' is protected for ${repo.name}. Choose another target branch to avoid PR Management targeting main.`;
        }
        if (!(branchesByRepositoryId[repo.id] || []).includes(repoTargetBranch)) {
          return `Target branch '${repoTargetBranch}' does not exist in ${repo.name}.`;
        }
        const sourceBranchName = getRepositorySourceBranch(repo);
        if (sourceBranchMode === "USE_EXISTING" && !sourceBranchName.trim()) {
          return `Select an existing source branch for ${repo.name}.`;
        }
        if (sourceBranchName.trim()) {
          const sourceBranchValidation = validateBranchName(sourceBranchName, `${repo.name} source branch`);
          if (sourceBranchValidation) return sourceBranchValidation;
          if (isProtectedSourceBranch(sourceBranchName)) {
            return `Source branch '${sourceBranchName.trim()}' is protected for ${repo.name}. Choose another source branch to avoid direct changes on main.`;
          }
          if (sourceBranchName.trim() === repoTargetBranch) {
            return `Source branch must be different from target branch for ${repo.name}.`;
          }
          if (
            sourceBranchMode === "USE_EXISTING" &&
            !(branchesByRepositoryId[repo.id] || []).includes(sourceBranchName.trim())
          ) {
            return `Source branch '${sourceBranchName.trim()}' does not exist in ${repo.name}.`;
          }
          if (
            sourceBranchMode === "CREATE_NEW" &&
            (branchesByRepositoryId[repo.id] || []).includes(sourceBranchName.trim())
          ) {
            return `Source branch '${sourceBranchName.trim()}' already exists in ${repo.name}. Choose 'Use existing branch' or enter a different new branch name.`;
          }
        }
      }
    }
    if (changeSourceType === "CLONE_PR") {
      if (!sourcePullRequestUrl.trim()) return "Source PR URL is required.";
      if (!sourcePullRequestUrl.includes("/_git/") || !sourcePullRequestUrl.includes("/pullrequest/")) {
        return "Source PR URL should be an Azure DevOps pull request URL.";
      }
    } else {
      if (!title.trim()) return "PR title is required.";
      for (const fileChange of fileChanges) {
        if (!fileChange.filePath.trim()) return "Every file change needs a file path.";
        if (operation === "CREATE" && !fileChange.content) {
          return "Create operation needs file content.";
        }
        if (operation === "UPDATE" && fileChange.changeType === "FULL_FILE_REPLACE" && !fileChange.content) {
          return "Full file replace needs file content.";
        }
        if (
          operation === "UPDATE" &&
          ["FIND_AND_REPLACE", "PATTERN_REPLACE"].includes(fileChange.changeType)
        ) {
          if (!fileChange.sourceContent) return "Replace operation needs source content.";
          if (fileChange.replacementContent === undefined || fileChange.replacementContent === null) {
            return "Replace operation needs replacement content.";
          }
        }
      }
    }

    return "";
  };

  const validateBranchName = (branchName, label) => {
    const trimmedBranchName = branchName.trim();
    if (/\s/.test(trimmedBranchName)) return `${label} cannot contain spaces.`;
    if (trimmedBranchName.includes("..")) return `${label} cannot contain '..'.`;
    if (trimmedBranchName.startsWith("/") || trimmedBranchName.endsWith("/")) {
      return `${label} cannot start or end with '/'.`;
    }
    if (trimmedBranchName.startsWith("refs/heads/")) {
      return `${label} should not include 'refs/heads/'.`;
    }
    return "";
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (
      selectedRepositories.length > MANY_REPOS_THRESHOLD &&
      !window.confirm(`You selected ${selectedRepositories.length} repositories. Continue creating PRs?`)
    ) {
      return;
    }

    setSubmitting(true);
    setError("");
    setCopyMessage("");
    setResult(null);
    try {
      const payload = {
        repositories: selectedRepositories.map((repo) => (
          changeSourceType === "CLONE_PR"
            ? { id: repo.id, name: repo.name }
            : {
                id: repo.id,
                name: repo.name,
                sourceBranchName: getRepositorySourceBranch(repo),
                targetBranch: getRepositoryTargetBranch(repo),
              }
        )),
        operation,
        changeSourceType,
        sourcePullRequestUrl,
        targetBranch,
        sourceBranch: changeSourceType === "CLONE_PR"
          ? null
          : {
              mode: sourceBranchMode,
              name: sourceBranchMode === "USE_EXISTING" ? existingSourceBranch : newSourceBranchName,
              resetFromTarget: sourceBranchMode === "USE_EXISTING" && resetSourceBranchFromTarget,
            },
        title,
        description,
        fileChanges: changeSourceType === "CLONE_PR"
          ? []
          : fileChanges.map(({ id, ...fileChange }) => ({
              ...fileChange,
              encoded: true,
              sourceContent: encodeText(fileChange.sourceContent),
              replacementContent: encodeText(fileChange.replacementContent),
              content: encodeText(fileChange.content),
            })),
      };
      setResult(await managePrFiles(payload));
    } catch (submitError) {
      setError(getErrorMessage(submitError, { changeSourceType, sourcePullRequestUrl }));
    } finally {
      setSubmitting(false);
      refreshBranches();
    }
  };

  const copyPrSummary = async () => {
    if (!prSummary) return;

    try {
      await navigator.clipboard.writeText(prSummary);
      setCopyMessage("PR summary copied.");
    } catch (copyError) {
      setCopyMessage("Copy failed. Select the summary text manually.");
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <BaseBox sx={{ width: "100%", maxWidth: 1180, p: 2, my: 2, mx: "auto" }}>
        <BaseBox sx={{ mb: 3 }}>
          <BaseTypography variant="h4" sx={{ fontWeight: 700 }}>
            PR Management
          </BaseTypography>
          <BaseTypography sx={{ color: "text.secondary", mt: 0.5 }}>
            Manage file changes across PAF repositories through Azure DevOps pull requests.
          </BaseTypography>
        </BaseBox>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <BaseBox
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <BaseBox
            sx={{
              border: "1px solid #ddd",
              borderRadius: 2,
              p: 2,
              backgroundColor: "background.paper",
            }}
          >
            <BaseTypography sx={{ fontWeight: 700, mb: 1 }}>Repositories</BaseTypography>
            <BaseTextField
              size="small"
              label="Search repositories"
              value={repositorySearch}
              onChange={(event) => setRepositorySearch(event.target.value)}
            />
            <BaseBox sx={{ display: "flex", gap: 1, my: 1 }}>
              <BaseButton
                fullWidth={false}
                variant="outlined"
                onClick={() => setSelectedRepositoryIds(filteredRepositories.map((repo) => repo.id))}
                disabled={filteredRepositories.length === 0}
              >
                Select visible
              </BaseButton>
              <BaseButton
                fullWidth={false}
                variant="outlined"
                onClick={() => setSelectedRepositoryIds([])}
              >
                Clear
              </BaseButton>
            </BaseBox>
            <BaseBox sx={{ maxHeight: 560, overflowY: "auto", pr: 1 }}>
              {loading ? (
                <BaseBox sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress size={28} />
                </BaseBox>
              ) : (
                filteredRepositories.map((repo) => (
                  <BaseCheckbox
                    key={repo.id}
                    checked={selectedRepositoryIds.includes(repo.id)}
                    onChange={() => toggleRepository(repo.id)}
                    label={repo.name}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: selectedRepositoryIds.includes(repo.id)
                        ? "#eef6ff"
                        : "transparent",
                    }}
                  />
                ))
              )}
            </BaseBox>
          </BaseBox>

          <BaseBox
            sx={{
              border: "1px solid #ddd",
              borderRadius: 2,
              p: 2,
              backgroundColor: "background.paper",
            }}
          >
            <BaseBox sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Chip
                label={`${selectedRepositories.length} selected`}
                color="primary"
                variant="outlined"
                onClick={() => setShowSelectedRepositories((current) => !current)}
                disabled={selectedRepositories.length === 0}
                clickable
              />
              <Chip label={changeSourceType === "CLONE_PR" ? "Clone PR" : operation} variant="outlined" />
              {changeSourceType !== "CLONE_PR" && <Chip label="Branches per repo" variant="outlined" />}
            </BaseBox>
            {showSelectedRepositories && selectedRepositories.length > 0 && (
              <BaseBox sx={{ border: "1px solid #ddd", borderRadius: 2, p: 1.5, mb: 2 }}>
                <BaseTypography sx={{ fontWeight: 700, mb: 1 }}>Selected repositories</BaseTypography>
                <BaseBox sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {selectedRepositories.map((repo) => (
                    <Chip
                      key={repo.id}
                      label={repo.name}
                      variant="outlined"
                      onDelete={() => toggleRepository(repo.id)}
                    />
                  ))}
                </BaseBox>
              </BaseBox>
            )}

            <BaseBox sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <BaseTextField
                select
                SelectProps={{ native: true }}
                label="Change source"
                value={changeSourceType}
                onChange={(event) => {
                  setChangeSourceType(event.target.value);
                  setResult(null);
                }}
              >
                {CHANGE_SOURCE_TYPES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </BaseTextField>
              {changeSourceType === "MANUAL" && (
                <BaseTextField
                  select
                  SelectProps={{ native: true }}
                  label="Operation"
                  value={operation}
                  onChange={(event) => {
                    setOperation(event.target.value);
                    setResult(null);
                  }}
                >
                  {OPERATIONS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </BaseTextField>
              )}
            </BaseBox>

            {changeSourceType !== "CLONE_PR" && (
              <BaseBox sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <BaseTextField
                  select
                  SelectProps={{ native: true }}
                  label="Source branch mode"
                  value={sourceBranchMode}
                  onChange={(event) => setSourceBranchMode(event.target.value)}
                >
                  {SOURCE_BRANCH_MODES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </BaseTextField>
              </BaseBox>
            )}
            {changeSourceType !== "CLONE_PR" && sourceBranchMode === "USE_EXISTING" && (
              <BaseBox sx={{ mt: 1 }}>
                <BaseCheckbox
                  checked={resetSourceBranchFromTarget}
                  onChange={(event) => setResetSourceBranchFromTarget(event.target.checked)}
                  label="Reset source branch from target before applying changes"
                />
              </BaseBox>
            )}

            {changeSourceType !== "CLONE_PR" && selectedRepositories.length > 0 && (
              <BaseBox sx={{ border: "1px solid #ddd", borderRadius: 2, p: 1.5, mt: 2 }}>
                <BaseBox sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center", mb: 1 }}>
                  <BaseTypography sx={{ fontWeight: 700 }}>Branches by repository</BaseTypography>
                  <BaseButton
                    fullWidth={false}
                    variant="outlined"
                    size="small"
                    onClick={refreshBranches}
                    disabled={branchesLoading}
                  >
                    Refresh branches
                  </BaseButton>
                </BaseBox>
                <BaseBox sx={{ display: "grid", gap: 1 }}>
                  {selectedRepositories.map((repo) => (
                    <BaseBox
                      key={repo.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 240px 240px" },
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Chip label={repo.name} variant="outlined" />
                      {sourceBranchMode === "USE_EXISTING" ? (
                        <BaseTextField
                          select
                          SelectProps={{ native: true }}
                          size="small"
                          label="Source branch"
                          value={getRepositorySourceBranch(repo)}
                          onChange={(event) => updateRepositorySourceBranch(repo.id, event.target.value)}
                          disabled={branchesLoading || getSourceBranchOptions(repo).length === 0}
                        >
                          {getSourceBranchOptions(repo).map((branch) => (
                            <option key={branch} value={branch}>
                              {branch}
                            </option>
                          ))}
                        </BaseTextField>
                      ) : (
                        <BaseTextField
                          size="small"
                          label="Source branch"
                          value={getRepositorySourceBranch(repo)}
                          onChange={(event) => updateRepositorySourceBranch(repo.id, event.target.value)}
                          placeholder="Leave empty to auto-generate"
                        />
                      )}
                      <BaseTextField
                        select
                        SelectProps={{ native: true }}
                        size="small"
                        label="Target branch"
                        value={getRepositoryTargetBranch(repo)}
                        onChange={(event) => updateRepositoryTargetBranch(repo.id, event.target.value)}
                        disabled={branchesLoading || getTargetBranchOptions(repo).length === 0}
                      >
                        {getTargetBranchOptions(repo).map((branch) => (
                          <option key={branch} value={branch}>
                            {branch}
                          </option>
                        ))}
                      </BaseTextField>
                    </BaseBox>
                  ))}
                </BaseBox>
              </BaseBox>
            )}

            <BaseTextField
              label="PR title"
              value={title}
              helperText={suggestedTitle && !titleManuallyEdited ? "Auto-filled from the first file change." : ""}
              onChange={(event) => {
                setTitle(event.target.value);
                setTitleManuallyEdited(true);
              }}
            />
            <BaseTextField
              label="PR description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              multiline
              minRows={3}
            />
            {changeSourceType === "CLONE_PR" && (
              <>
                <BaseTextField
                  label="Source PR URL"
                  value={sourcePullRequestUrl}
                  onChange={(event) => setSourcePullRequestUrl(event.target.value)}
                  placeholder="https://dev.azure.com/VFCloudEngineering/VF_Itaap/_git/repository-name/pullrequest/12345"
                />
                {(sourcePullRequestPreviewLoading || sourcePullRequestPreview || sourcePullRequestPreviewError) && (
                  <BaseBox sx={{ border: "1px solid #ddd", borderRadius: 2, p: 1.5, mt: 1 }}>
                    <BaseTypography sx={{ fontWeight: 700, mb: 1 }}>Source PR details</BaseTypography>
                    {sourcePullRequestPreviewLoading && (
                      <BaseBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={18} />
                        <BaseTypography sx={{ color: "text.secondary" }}>Loading source PR details...</BaseTypography>
                      </BaseBox>
                    )}
                    {!sourcePullRequestPreviewLoading && sourcePullRequestPreviewError && (
                      <Alert severity="error">{sourcePullRequestPreviewError}</Alert>
                    )}
                    {!sourcePullRequestPreviewLoading && sourcePullRequestPreview && (
                      <BaseBox sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Chip label={`Repo: ${sourcePullRequestPreview.repositoryName}`} variant="outlined" />
                        <Chip label={`PR: ${sourcePullRequestPreview.pullRequestId}`} variant="outlined" />
                        <Chip label={`Source: ${sourcePullRequestPreview.sourceBranch}`} variant="outlined" />
                        <Chip label={`Target: ${sourcePullRequestPreview.targetBranch}`} variant="outlined" />
                        {sourcePullRequestPreview.title && (
                          <Chip label={`Title: ${sourcePullRequestPreview.title}`} variant="outlined" />
                        )}
                      </BaseBox>
                    )}
                  </BaseBox>
                )}
              </>
            )}

            {changeSourceType === "MANUAL" && (
              <>
                <Divider sx={{ my: 2 }} />
                <BaseBox sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
                  <BaseTypography sx={{ fontWeight: 700 }}>File changes</BaseTypography>
                  <BaseButton fullWidth={false} variant="outlined" onClick={() => setFileChanges((current) => [...current, newFileChange()])}>
                    Add file change
                  </BaseButton>
                </BaseBox>

                {fileChanges.map((fileChange, index) => (
                  <BaseBox
                    key={fileChange.id}
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 2,
                      p: 2,
                      mt: 2,
                    }}
                  >
                    <BaseBox sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                      <BaseTypography sx={{ fontWeight: 700 }}>File change {index + 1}</BaseTypography>
                      <BaseButton
                        fullWidth={false}
                        variant="outlined"
                        color="error"
                        onClick={() => removeFileChange(fileChange.id)}
                        disabled={fileChanges.length === 1}
                      >
                        Remove
                      </BaseButton>
                    </BaseBox>
                    <BaseTextField
                      label="File path"
                      value={fileChange.filePath}
                      onChange={(event) => updateFileChange(fileChange.id, "filePath", event.target.value)}
                      placeholder="pom.xml"
                    />
                    {operation === "UPDATE" && (
                      <BaseTextField
                        select
                        SelectProps={{ native: true }}
                        label="Change type"
                        value={fileChange.changeType}
                        onChange={(event) => updateFileChange(fileChange.id, "changeType", event.target.value)}
                      >
                        {CHANGE_TYPES.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </BaseTextField>
                    )}
                    {(operation === "CREATE" ||
                      (operation === "UPDATE" && fileChange.changeType === "FULL_FILE_REPLACE")) && (
                      <BaseTextField
                        label="File content"
                        value={fileChange.content}
                        onChange={(event) => updateFileChange(fileChange.id, "content", event.target.value)}
                        multiline
                        minRows={8}
                      />
                    )}
                    {operation === "UPDATE" && ["FIND_AND_REPLACE", "PATTERN_REPLACE"].includes(fileChange.changeType) && (
                      <BaseBox sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                        <BaseTextField
                          label={fileChange.changeType === "PATTERN_REPLACE" ? "Source regex pattern" : "Source content"}
                          value={fileChange.sourceContent}
                          onChange={(event) => updateFileChange(fileChange.id, "sourceContent", event.target.value)}
                          multiline
                          minRows={8}
                          placeholder={fileChange.changeType === "PATTERN_REPLACE"
                            ? "<artifactId>apifactory-cache</artifactId>\\s*<version>[^<]+</version>"
                            : "<version>1.6.0-SNAPSHOT</version>"}
                        />
                        <BaseTextField
                          label="Replacement content"
                          value={fileChange.replacementContent}
                          onChange={(event) => updateFileChange(fileChange.id, "replacementContent", event.target.value)}
                          multiline
                          minRows={8}
                          placeholder="<version>1.10.0-SNAPSHOT</version>"
                        />
                      </BaseBox>
                    )}
                  </BaseBox>
                ))}
              </>
            )}

            <BaseBox sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
              <BaseButton
                fullWidth={false}
                variant="outlined"
                onClick={() => {
                  setFileChanges([newFileChange()]);
                  setTitle("");
                  setTitleManuallyEdited(false);
                  setDescription("");
                  setNewSourceBranchName("");
                  setResetSourceBranchFromTarget(true);
                  setSourcePullRequestUrl("");
                  setSourcePullRequestPreview(null);
                  setSourcePullRequestPreviewError("");
                  setChangeSourceType("MANUAL");
                  setResult(null);
                  setError("");
                }}
              >
                Reset form
              </BaseButton>
              <BaseButton fullWidth={false} onClick={submit} disabled={submitting || loading || branchesLoading}>
                {submitting ? "Running..." : "Create PR"}
              </BaseButton>
            </BaseBox>

            {result && (
              <BaseBox sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <BaseTypography sx={{ fontWeight: 700, mb: 1 }}>Results</BaseTypography>
                {sortedRepositoryResults.length > 0 && (
                  <BaseBox sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    <Chip label={`Total ${resultCounts.total}`} variant="outlined" />
                    <Chip label={`Created ${resultCounts.created}`} color="success" variant="outlined" />
                    <Chip label={`Failed ${resultCounts.failed}`} color="error" variant="outlined" />
                    <Chip label={`Skipped ${resultCounts.skipped}`} color="warning" variant="outlined" />
                    {resultCounts.other > 0 && <Chip label={`Other ${resultCounts.other}`} variant="outlined" />}
                  </BaseBox>
                )}
                {prSummary && (
                  <BaseBox sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2, mb: 2 }}>
                    <BaseBox sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
                      <BaseTypography sx={{ fontWeight: 700 }}>PR URLs for manager approval</BaseTypography>
                      <BaseButton fullWidth={false} variant="outlined" onClick={copyPrSummary}>
                        Copy summary
                      </BaseButton>
                    </BaseBox>
                    {copyMessage && (
                      <BaseTypography sx={{ color: "text.secondary", mt: 1 }}>
                        {copyMessage}
                      </BaseTypography>
                    )}
                    <BaseTextField
                      value={prSummary}
                      fullWidth
                      multiline
                      minRows={Math.min(8, Math.max(3, prSummary.split("\n").length))}
                      InputProps={{ readOnly: true }}
                    />
                  </BaseBox>
                )}
                {sortedRepositoryResults.map((repoResult) => (
                  <Alert
                    key={`${repoResult.repositoryId}-${repoResult.branchName}`}
                    severity={repoResult.status === "CREATED" ? "success" : repoResult.status === "FAILED" ? "error" : "info"}
                    sx={{ mb: 1 }}
                  >
                    <strong>{repoResult.repositoryName}</strong>: {repoResult.message}
                    {repoResult.pullRequestUrl && (
                      <>
                        {" "}
                        <Link href={repoResult.pullRequestUrl} target="_blank" rel="noreferrer">
                          Open PR #{repoResult.pullRequestId}
                        </Link>
                      </>
                    )}
                    {!repoResult.pullRequestUrl && repoResult.repositoryUrl && (
                      <>
                        {" "}
                        <Link href={repoResult.repositoryUrl} target="_blank" rel="noreferrer">
                          Open repository
                        </Link>
                      </>
                    )}
                  </Alert>
                ))}
              </BaseBox>
            )}
          </BaseBox>
        </BaseBox>
      </BaseBox>
    </>
  );
}

export default PrManagement;
